import {
  TrackingNetworkStatus,
  TrackingRecentApiFailure,
  TrackingRecentApiSuccess,
  TrackingRecentError,
  TrackingTechnicalState,
} from '../types/Tracking/types';
import { debugTrackingLog } from './tracking';
import { emitTrackingRealtimeLog } from './trackingRealtime';

const MAX_RECENT_ERRORS = 10;
const MAX_RECENT_API_FAILURES = 10;
const MAX_RECENT_API_SUCCESSES = 10;

type TrackingTechnicalSubscriber = (state: TrackingTechnicalState) => void;

const subscribers = new Set<TrackingTechnicalSubscriber>();

const createInitialTrackingTechnicalState = (): TrackingTechnicalState => ({
  currentRoute: null,
  previousRoute: null,
  networkStatus: 'unknown',
  lastSuccessfulAction: null,
  lastFailedAction: null,
  recentErrors: [],
  recentApiFailures: [],
  recentApiSuccesses: [],
});

const cloneTechnicalState = (state: TrackingTechnicalState): TrackingTechnicalState => ({
  ...state,
  recentErrors: [...state.recentErrors],
  recentApiFailures: [...state.recentApiFailures],
  recentApiSuccesses: [...state.recentApiSuccesses],
});

let technicalState = createInitialTrackingTechnicalState();

const notifySubscribers = (): void => {
  const snapshot = cloneTechnicalState(technicalState);
  subscribers.forEach((listener) => listener(snapshot));
};

const setTechnicalState = (nextState: TrackingTechnicalState): void => {
  technicalState = nextState;
  notifySubscribers();
};

const keepLastItems = <T>(items: T[], nextItem: T, maxItems: number): T[] => {
  const nextItems = [...items, nextItem];

  if (nextItems.length <= maxItems) return nextItems;

  return nextItems.slice(nextItems.length - maxItems);
};

export const resolveTrackingNetworkStatus = (): TrackingNetworkStatus => {
  if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') {
    return 'unknown';
  }

  return navigator.onLine ? 'online' : 'offline';
};

export const getTrackingTechnicalState = (): TrackingTechnicalState =>
  cloneTechnicalState(technicalState);

export const subscribeTrackingTechnicalState = (
  listener: TrackingTechnicalSubscriber,
): (() => void) => {
  subscribers.add(listener);

  return () => {
    subscribers.delete(listener);
  };
};

export const resetTrackingTechnicalState = (): void => {
  setTechnicalState({
    ...createInitialTrackingTechnicalState(),
    networkStatus: resolveTrackingNetworkStatus(),
  });
};

export const hydrateTrackingTechnicalState = (
  partialState: Partial<TrackingTechnicalState> | null | undefined,
): void => {
  const nextState: TrackingTechnicalState = {
    ...createInitialTrackingTechnicalState(),
    networkStatus: resolveTrackingNetworkStatus(),
    ...partialState,
    recentErrors: [...(partialState?.recentErrors ?? [])].slice(-MAX_RECENT_ERRORS),
    recentApiFailures: [...(partialState?.recentApiFailures ?? [])].slice(
      -MAX_RECENT_API_FAILURES,
    ),
    recentApiSuccesses: [...(partialState?.recentApiSuccesses ?? [])].slice(
      -MAX_RECENT_API_SUCCESSES,
    ),
  };

  setTechnicalState(nextState);
};

export const setTrackingRouteState = (currentRoute: string | null): void => {
  const normalizedRoute = currentRoute || null;

  if (technicalState.currentRoute === normalizedRoute) return;

  setTechnicalState({
    ...technicalState,
    previousRoute: technicalState.currentRoute,
    currentRoute: normalizedRoute,
  });
};

export const setTrackingNetworkStatus = (networkStatus: TrackingNetworkStatus): void => {
  if (technicalState.networkStatus === networkStatus) return;

  setTechnicalState({
    ...technicalState,
    networkStatus,
  });
};

export const recordTrackingSuccessfulAction = (action: string): void => {
  if (!action || technicalState.lastSuccessfulAction === action) return;

  setTechnicalState({
    ...technicalState,
    lastSuccessfulAction: action,
  });

  emitTrackingRealtimeLog({
    type: 'success',
    title: action,
    detail: 'Acción completada correctamente',
    timestamp: new Date().toISOString(),
    sessionId: null,
    userKey: null,
    route: technicalState.currentRoute ?? null,
  });
};

export const recordTrackingFailedAction = (action: string): void => {
  if (!action || technicalState.lastFailedAction === action) return;

  setTechnicalState({
    ...technicalState,
    lastFailedAction: action,
  });
};

export const recordTrackingRecentError = (
  payload: Omit<TrackingRecentError, 'timestamp'> & { timestamp?: string },
): void => {
  const nextError: TrackingRecentError = {
    ...payload,
    timestamp: payload.timestamp ?? new Date().toISOString(),
  };

  setTechnicalState({
    ...technicalState,
    recentErrors: keepLastItems(technicalState.recentErrors, nextError, MAX_RECENT_ERRORS),
  });

  debugTrackingLog('Error tecnico registrado', {
    message: nextError.message,
    source: nextError.source,
  });

  emitTrackingRealtimeLog({
    type: 'error',
    title: nextError.message,
    detail: nextError.source ?? null,
    timestamp: nextError.timestamp,
    sessionId: null,
    userKey: null,
    route: technicalState.currentRoute ?? null,
  });
};

export const normalizeTrackingApiEndpoint = (endpoint: string | undefined): string => {
  if (!endpoint) return 'unknown';

  try {
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
      return new URL(endpoint).pathname || endpoint;
    }
  } catch {
    return endpoint;
  }

  return endpoint;
};

export const buildTrackingHttpActionLabel = (method: string | undefined, endpoint: string): string => {
  const normalizedMethod = (method ?? 'GET').toUpperCase();
  const normalizedEndpoint = normalizeTrackingApiEndpoint(endpoint);

  return `${normalizedMethod} ${normalizedEndpoint}`;
};

export const recordTrackingApiSuccess = (
  payload: Omit<TrackingRecentApiSuccess, 'timestamp'> & {
    timestamp?: string;
  },
): void => {
  const nextSuccess: TrackingRecentApiSuccess = {
    endpoint: normalizeTrackingApiEndpoint(payload.endpoint),
    method: payload.method.toUpperCase(),
    status: payload.status ?? 204,
    durationMs: payload.durationMs,
    timestamp: payload.timestamp ?? new Date().toISOString(),
  };

  setTechnicalState({
    ...technicalState,
    lastSuccessfulAction: buildTrackingHttpActionLabel(
      nextSuccess.method,
      nextSuccess.endpoint,
    ),
    recentApiSuccesses: keepLastItems(
      technicalState.recentApiSuccesses,
      nextSuccess,
      MAX_RECENT_API_SUCCESSES,
    ),
  });

  debugTrackingLog('Éxito de API registrado', nextSuccess);

  emitTrackingRealtimeLog({
    type: 'success',
    title: `${nextSuccess.method} ${nextSuccess.endpoint}`,
    detail: `Status ${nextSuccess.status}`,
    timestamp: nextSuccess.timestamp,
    sessionId: null,
    userKey: null,
    route: technicalState.currentRoute ?? null,
  });
};

export const recordTrackingApiFailure = (
  payload: Omit<TrackingRecentApiFailure, 'timestamp'> & {
    action?: string;
    timestamp?: string;
  },
): void => {
  const nextFailure: TrackingRecentApiFailure = {
    endpoint: normalizeTrackingApiEndpoint(payload.endpoint),
    method: payload.method.toUpperCase(),
    status: payload.status,
    durationMs: payload.durationMs,
    errorMessage: payload.errorMessage,
    timestamp: payload.timestamp ?? new Date().toISOString(),
  };
  const action =
    payload.action ?? buildTrackingHttpActionLabel(nextFailure.method, nextFailure.endpoint);

  setTechnicalState({
    ...technicalState,
    lastFailedAction: action,
    recentApiFailures: keepLastItems(
      technicalState.recentApiFailures,
      nextFailure,
      MAX_RECENT_API_FAILURES,
    ),
  });

  debugTrackingLog('Fallo de API registrado', nextFailure);

  emitTrackingRealtimeLog({
    type: 'api',
    title: `${nextFailure.method} ${nextFailure.endpoint}`,
    detail: nextFailure.errorMessage ?? String(nextFailure.status ?? ''),
    timestamp: nextFailure.timestamp,
    sessionId: null,
    userKey: null,
    route: technicalState.currentRoute ?? null,
  });
};

declare global {
  interface Window {
    __fielpetTrackingDiagnostics?: {
      status: () => TrackingTechnicalState;
      reset: () => void;
      setNetworkStatus: (networkStatus: TrackingNetworkStatus) => void;
      recordSuccess: (action: string) => void;
      recordFailure: (action: string) => void;
      emitError: (message: string) => void;
      emitApiFailure: (payload: {
        endpoint: string;
        method?: string;
        status?: number | null;
        durationMs?: number | null;
        errorMessage?: string | null;
      }) => void;
      emitApiSuccess: (payload: {
        endpoint: string;
        method?: string;
        status?: number;
        durationMs?: number | null;
      }) => void;
    };
  }
}

export const attachTrackingDiagnosticsDebugApi = (): void => {
  if (typeof window === 'undefined') return;

  window.__fielpetTrackingDiagnostics = {
    status: () => getTrackingTechnicalState(),
    reset: () => resetTrackingTechnicalState(),
    setNetworkStatus: (networkStatus) => setTrackingNetworkStatus(networkStatus),
    recordSuccess: (action) => recordTrackingSuccessfulAction(action),
    recordFailure: (action) => recordTrackingFailedAction(action),
    emitError: (message) =>
      recordTrackingRecentError({
        message,
        source: 'manual',
        stack: null,
      }),
    emitApiFailure: (payload) =>
      recordTrackingApiFailure({
        endpoint: payload.endpoint,
        method: payload.method ?? 'GET',
        status: payload.status ?? null,
        durationMs: payload.durationMs ?? null,
        errorMessage: payload.errorMessage ?? null,
      }),
    emitApiSuccess: (payload) =>
      recordTrackingApiSuccess({
        endpoint: payload.endpoint,
        method: payload.method ?? 'GET',
        status: payload.status ?? 204,
        durationMs: payload.durationMs ?? null,
      }),
  };
};
